/**
 * L'IDENTIFIANT D'UNE SOCIÉTÉ — ce qui la distingue de toutes les autres du domaine.
 *
 * ⚠️ C'EST LA CLÉ DE DÉDOUBLONNAGE, PAS UN CHAMP DE PLUS. Une société n'appartient
 * pas à l'événement mais au DOMAINE : deux participants du même salon qui viennent de
 * la même maison doivent tomber sur la même fiche, sinon l'organisateur relit sa liste
 * d'exposants en double. Le serveur le tient d'ailleurs pour acquis — il refuse un
 * `siren` déjà pris ailleurs dans le domaine (`SIRET_ALREADY_IN_USE_MESSAGE`,
 * `api/src/routes/forms/updateData/index.js`). D'où un écran entier consacré à le
 * saisir AVANT de proposer d'en créer une.
 *
 * ⚠️ LA COLONNE S'APPELLE `siren`, LE CHAMP NON. C'est un nom français dans une base
 * qui sert aussi des salons italiens et suisses ; la V1 le rend donc sous un intitulé
 * réglable (`COMPANY_INDEX_LABEL`), et sous un format par pays quand `FO_INDEX_INTER`
 * est allumé. Les deux modes sont repris tels quels, y compris leur incohérence
 * assumée : hors mode international, le défaut est un SIREN (9 chiffres) ; en mode
 * international, la France est un SIRET (14). C'est ce que les deux écrans de la V1
 * annoncent, et le changer renommerait ce que des organisateurs ont déjà saisi.
 *
 * Ce fichier est PUR, et c'est pour ça qu'il est à part de `companies.ts` : la règle
 * qui décide si une saisie peut partir se teste sans réseau (voir
 * `companyIndex.test.ts`).
 */

import { french, type Say } from "../settings/say";

/** Le pays d'un identifiant — la valeur rangée dans `Companies.country`. */
export type Country = "FR" | "IT" | "CH" | "PT" | "PL" | "ES" | "OTHER";

/**
 * ⚠️ LA TABLE EST CELLE DE LA V1, RECOPIÉE SANS RETOUCHE
 * (`fo-meeting/src/screens/companies/indexes.js`). Ce sont des données du produit :
 * des organisateurs ont configuré leurs événements dessus, et un motif « amélioré »
 * ici refuserait un identifiant que la V1 accepte, sur la même base, pour les mêmes
 * sociétés.
 *
 * Les noms de pays sont dans LEUR langue, comme chez elle : quelqu'un qui cherche la
 * Suisse dans une liste cherche « Schweiz » aussi souvent que « Suisse ».
 */
export const COUNTRIES: Readonly<
  Record<
    Country,
    {
      name: string;
      label: string;
      pattern: RegExp;
      example: string;
      /* Les seuls mots FRANÇAIS de la table, donc les seuls à traduire : les autres
         sont dans la langue de leur pays, exprès. `t()` les lit avec le mot pour repli. */
      nameKey?: string;
      labelKey?: string;
    }
  >
> = {
  FR: {
    name: "France",
    label: "SIRET de la société",
    labelKey: "COMPANY_INDEX_SIRET",
    pattern: /^\d{14}$/,
    example: "12345678912345",
  },
  IT: {
    name: "Italia",
    label: "Numero REA",
    pattern: /^[A-Z]{2}\d{6,9}$/,
    example: "AB123456",
  },
  CH: {
    name: "Schweiz",
    label: "Unternehmens-Identifikationsnummer",
    pattern: /^CHE-\d{3}\.\d{3}\.\d{3}$/,
    example: "CHE-123.456.789",
  },
  PT: {
    name: "Portugal",
    label: "Número de Identificação Fiscal (NIF)",
    pattern: /^\d{9}$/,
    example: "123456789",
  },
  PL: {
    name: "Polska",
    label: "Numer REGON",
    pattern: /^\d{9,14}$/,
    example: "123456789",
  },
  ES: {
    name: "España",
    label: "Número de Identificación Fiscal (NIF)",
    pattern: /^[XYZ]?\d{7}[A-Z]$/,
    example: "X1234567L",
  },
  /* Le fourre-tout de la V1 : tout est accepté, parce qu'un pays hors table vaut
     mieux qu'une inscription impossible. */
  OTHER: {
    name: "Autre pays",
    nameKey: "OTHER_COUNTRY",
    label: "Identifiant de la société dans votre pays",
    labelKey: "COMPANY_INDEX_OTHER_COUNTRY",
    pattern: /^.+$/,
    example: "",
  },
};

export const OFFERED_COUNTRIES = Object.keys(COUNTRIES) as ReadonlyArray<Country>;

/** La longueur exigée hors mode international, quand le réglage est vide. */
export const DEFAULT_SIZE = 9;

/** Ce que le champ d'identifiant annonce, accepte, et reproche. */
export type Index = {
  /** L'intitulé du champ. */
  label: string;
  /** Un vrai exemple, montré en filigrane — vide quand il n'y en a pas. */
  example: string;
  /** Ce que la frappe ne doit pas dépasser, s'il y a une longueur fixe. */
  taille?: number;
  /** La seule saisie acceptable. */
  pattern: RegExp;
  /** Ce qu'on dit quand elle ne l'est pas — sous le champ, jamais en tête. */
  complaint: string;
  /** Un caractère de pure mise en forme, retiré avant le contrôle — rien pour
      « autre pays », qui accepte tout tel quel. Sans drapeau `g` : il se teste
      caractère par caractère, et un `g` le rendrait changeant d'un appel à l'autre. */
  separator?: RegExp;
};

/* Les espaces et les points d'un identifiant tapé groupé (« 552 081 317 »). */
const SPACES_AND_DOTS = /[\s.]/;
/* Les espaces seuls, là où le point FAIT PARTIE du format (« CHE-123.456.789 »). */
const SPACES = /\s/;

/**
 * LA RÈGLE DU CHAMP, des réglages jusqu'au reproche.
 *
 * Un seul endroit la calcule : l'écran ne fait que l'afficher et l'appliquer. Écrite
 * en deux moitiés — l'intitulé ici, la vérification là — les deux se contrediraient,
 * et le reproche annoncerait un format que le champ n'exige pas.
 */
export function indexDe(
  {
    inter,
    country,
    label,
    taille,
  }: {
    /** Le mode international : le pays décide du format. */
    inter: boolean;
    country: Country;
    /** Le nom réglé par l'organisateur — ignoré en mode international, où chaque pays
        a le sien (« Numero REA » n'est pas « SIREN »). */
    label: string;
    /** Le nombre de chiffres exigé hors mode international. */
    taille?: number;
  },
  say: Say = french,
): Index {
  if (inter) {
    const p = COUNTRIES[country] ?? COUNTRIES.OTHER;
    return {
      label: p.labelKey ? say(p.labelKey, p.label) : p.label,
      example: p.example,
      pattern: p.pattern,
      separator:
        country === "OTHER"
          ? undefined
          : p.pattern.source.includes("\\.")
            ? SPACES
            : SPACES_AND_DOTS,
      /* Le pays est nommé dans le reproche : la première cause d'un format refusé
         n'est pas la faute de frappe, c'est le mauvais pays laissé sur France. */
      complaint: p.example
        ? say(
            "COMPANY_INDEX_INVALID",
            "Ce n'est pas un identifiant $1 valide. Exemple : $2.",
            [p.nameKey ? say(p.nameKey, p.name) : p.name, p.example],
          )
        : say("COMPANY_INDEX_EMPTY", "Cet identifiant ne peut pas être vide."),
    };
  }

  const n = Number.isFinite(taille) && (taille as number) > 0
    ? Math.floor(taille as number)
    : DEFAULT_SIZE;

  return {
    label,
    // Un exemple ENGENDRÉ à la bonne longueur : « 123456789 » pour 9. Un exemple fixe
    // mentirait dès que l'organisateur change la taille.
    example: Array.from({ length: n }, (_, i) => String((i % 9) + 1)).join(""),
    taille: n,
    pattern: new RegExp(`^\\d{${n}}$`),
    separator: SPACES_AND_DOTS,
    complaint: say(
      "COMPANY_INDEX_DIGITS",
      "Cet identifiant compte $1 chiffres, sans espace ni lettre.",
      [n],
    ),
  };
}

/**
 * LE REPROCHE À DIRE SOUS LE CHAMP, ou `null` si la saisie peut partir.
 *
 * ⚠️ VIDE EST FAUTIF ICI, contrairement aux champs du formulaire (voir
 * `checks.ts`) : sans identifiant il n'y a rien à chercher et rien à dédoublonner,
 * donc rien à faire de l'écran. C'est la règle de la V1, qui le marque `required`.
 */
export function checkIndex(
  typed: string,
  index: Index,
  say: Say = french,
): string | null {
  const own = cleanIndex(typed, index);
  if (own === "")
    return say("COMPANY_INDEX_REQUIRED", "Cet identifiant est nécessaire pour continuer.");
  return index.pattern.test(own) ? null : index.complaint;
}

/**
 * L'IDENTIFIANT TEL QU'IL PART — contrôlé, cherché, enregistré.
 *
 * ⚠️ « 552 081 317 » EST LA FAÇON HABITUELLE D'ÉCRIRE UN SIREN. Les espaces et les
 * points n'y sont que de la mise en forme : on les retire, sans quoi la saisie la plus
 * courante était refusée, et la même société cherchée sous deux écritures.
 */
export function cleanIndex(typed: string, index: Index): string {
  const own = typed.trim();
  const sep = index.separator;
  return sep ? [...own].filter((c) => !sep.test(c)).join("") : own;
}

/**
 * LA FRAPPE BORNÉE À LA LONGUEUR EXIGÉE — comptée sur le texte NETTOYÉ.
 *
 * ⚠️ PAS DE `maxLength` SUR LE CHAMP : il compte les espaces, et coupait
 * « 552 081 317 » en « 552 081 3 ». Ici, seuls les caractères qui comptent avancent
 * le compteur ; la mise en forme de la personne reste affichée telle quelle.
 */
export function capIndex(typed: string, index: Index): string {
  const { taille, separator: sep } = index;
  if (!taille) return typed;
  let count = 0;
  let end = 0;
  for (const c of typed) {
    const counts = !(sep?.test(c) ?? false) && c.trim() !== "";
    if (counts && count === taille) break;
    if (counts) count++;
    end += c.length;
  }
  return typed.slice(0, end);
}

/**
 * LE REFUS DE L'IDENTIFIANT PAR LE SERVEUR, EN FRANÇAIS — ou `null` pour autre chose.
 *
 * ⚠️ CE N'EST PAS UNE PANNE, ET « RÉESSAYEZ » Y RÉPOND FAUX. L'écran cherche pourtant
 * l'identifiant avant de proposer d'en créer une : le refus ne survient donc que si la
 * fiche a été créée entre-temps, par un autre participant du même salon — précisément
 * le cas que le dédoublonnage existe pour traiter. Le remède n'est pas de renvoyer la
 * même chose, c'est de refaire la recherche, qui trouvera cette fois la société à
 * rejoindre.
 *
 * Le message du serveur est un code nu (`SIRET_ALREADY_IN_USE_MESSAGE`, jeté par
 * `api/src/routes/forms/updateData/index.js`), qu'on ne montre à personne.
 */
export function identifierRefusal(
  error: unknown,
  say: Say = french,
): string | null {
  const message =
    error instanceof Error ? error.message : String(error ?? "");
  return /SIRET_ALREADY_IN_USE|SIREN_EXISTS/i.test(message)
    ? say(
        "COMPANY_INDEX_TAKEN",
        "Cet identifiant vient d'être pris par une autre fiche. Reprenez la recherche : vous pourrez vous rattacher à la société qui le porte.",
      )
    : null;
}
